import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined'
import Box from '@mui/material/Box'
import { amber } from '@mui/material/colors'
import { alpha } from '@mui/material/styles'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo, useState } from 'react'

import T from 'T'

const TEXT_FIELD_SX = { width: 110 }
const INPUT_PROPS = { disableUnderline: true }
// The picker lives on map surfaces that are often inside a Dialog (modal zIndex);
// lift the calendar popper to tooltip level so it clears the dialog + map overlays.
const getPopperSx = theme => ({ zIndex: theme.zIndex.tooltip })

const getRootSx = (theme, accentOverride) => {
  const isDark = theme.palette.mode === 'dark'
  const accent = accentOverride ?? (isDark ? amber[400] : theme.palette.warning.main)
  const { glass } = theme.palette
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    bgcolor: glass?.surface ?? alpha(theme.palette.background.paper, 0.92),
    // Light maps render the raw divider colour as a harsh black outline, so drop
    // the border there and lean on the shadow; dark keeps its border.
    border: isDark ? `1px solid ${glass?.border ?? alpha(theme.palette.divider, 0.6)}` : 'none',
    borderRadius: '4px',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    px: '10px',
    py: '5px',
    cursor: 'pointer',
    boxShadow: glass?.shadow ?? theme.shadows[2],
    '& .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root': {
      typography: 'overline',
      display: 'inline-flex',
      alignItems: 'center',
      bgcolor: 'transparent',
      color: accent,
      letterSpacing: '0.3px',
      textTransform: 'none',
      // lineHeight matches the label's body default so alignItems:'center'
      // on the parent flex puts both text rows on the same horizontal centerline.
      lineHeight: 1.5,
      cursor: 'pointer',
      border: 0,
      padding: 0,
      minHeight: 0,
      '& fieldset, & .MuiOutlinedInput-notchedOutline': { border: 0 },
      // MUI X Pickers render the visible date as <span> sections, not the <input> — style the spans directly.
      '& .MuiPickersSectionList-root, & .MuiPickersInputBase-sectionsContainer, & .MuiPickersInputBase-sectionContent': {
        ...theme.typography.overline,
        letterSpacing: '0.3px',
        textTransform: 'none',
        fontWeight: 400,
        lineHeight: 1.5,
      },
      // MUI X gives the sections container asymmetric padding (1px top / 5px bottom)
      // which leaves the text sitting 2 px above the picker-root centerline. Zero
      // the padding so the section spans share the picker root's centre, which in
      // turn matches the parent flex's `alignItems: 'center'` with the label.
      '& .MuiPickersSectionList-root, & .MuiPickersInputBase-sectionsContainer': {
        padding: 0,
      },
      '& input': {
        padding: 0,
        height: '1em',
        lineHeight: 'normal',
        cursor: 'pointer',
        color: 'inherit',
        boxSizing: 'content-box',
      },
      '& .MuiInputAdornment-root': {
        height: 'auto',
        maxHeight: 'none',
        ml: 0,
        '& .MuiButtonBase-root': {
          padding: 0,
          color: accent,
          '& .MuiSvgIcon-root': { fontSize: theme.typography.h5.fontSize },
        },
      },
    },
  }
}

const getIconSx = (theme, accentOverride) => ({
  fontSize: theme.typography.h6.fontSize,
  opacity: 0.85,
  color: accentOverride ?? (theme.palette.mode === 'dark' ? amber[400] : theme.palette.warning.main),
})

const getLabelSx = theme => ({
  color: theme.palette.glass?.textMedium ?? theme.palette.text.secondary,
  // Match the picker's typography + line-height so the label baseline and the
  // MM/DD/YYYY section baseline land on the same horizontal line.
  ...theme.typography.overline,
  letterSpacing: '0.3px',
  textTransform: 'none',
  fontWeight: 400,
  lineHeight: 1.5,
})

const DriverDatePicker = ({ value, onChange, maxDate, label = T.DATE, accentColor }) => {
  const [open, setOpen] = useState(false)
  const handleOpen = useCallback(() => setOpen(true), [])
  const handleClose = useCallback(() => setOpen(false), [])
  const rootSx = useCallback(theme => getRootSx(theme, accentColor), [accentColor])
  const iconSx = useCallback(theme => getIconSx(theme, accentColor), [accentColor])
  const slotProps = useMemo(
    () => ({
      textField: {
        onClick: handleOpen,
        size: 'small',
        variant: 'standard',
        InputProps: INPUT_PROPS,
        sx: TEXT_FIELD_SX,
      },
      popper: {
        sx: getPopperSx,
      },
    }),
    [handleOpen]
  )

  return (
    <Box sx={rootSx} onClick={handleOpen}>
      <TimelineOutlinedIcon sx={iconSx} />
      <Box component="span" sx={getLabelSx}>
        {label}
      </Box>
      <DesktopDatePicker
        disableScrollLock
        maxDate={maxDate}
        open={open}
        slotProps={slotProps}
        value={value}
        onChange={onChange}
        onClose={handleClose}
        onOpen={handleOpen}
      />
    </Box>
  )
}

DriverDatePicker.propTypes = {
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
  onChange: PropTypes.func.isRequired,
  maxDate: PropTypes.instanceOf(Date),
  label: PropTypes.string,
  accentColor: PropTypes.string,
}

export default DriverDatePicker
